import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { apiFetch } from "../api/client";
import { DateRangePicker, defaultRange } from "../components/DateRangePicker";
import {
  computeDashboardCards, computeRejectionCards, fintracBannerText, isRejectionsSummary,
  type DashboardSummary, type RejectionsSummary,
} from "./dashboardStats";

interface StatCardProps {
  label: string; value: number; sub?: string; tone?: "accent" | "warn" | "bad";
  /** When set the whole card is a link -- a number worth worrying about
   *  should be one click from the rows behind it. */
  to?: string;
}

function StatCard({ label, value, sub, tone, to }: StatCardProps) {
  const body = (
    <>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </>
  );
  const className = `card stat-card ${tone ?? ""}${to ? " clickable" : ""}`;
  return to
    ? <Link to={to} className={className} aria-label={`${label}: ${value}`}>{body}</Link>
    : <div className={className}>{body}</div>;
}

/** What summarize_aggregate() returns for an internal caller with no
 *  ?client_id=. Note there is no by_day series -- see AggregateView. */
interface AggregateSummary {
  statistics: {
    accepted: { count: number; total_cad: number };
    submitted_to_fintrac: { report_count: number; transaction_count: number; total_cad: number };
    pending: { report_count: number; transaction_count: number; total_cad: number };
  };
  by_client: Array<{
    client_id: string;
    accepted: { count: number; total_cad: number };
    submitted: { report_count: number; transaction_count: number; total_cad: number };
    pending: { report_count: number; transaction_count: number; total_cad: number };
    errors: number;
  }>;
}

const statRow = { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, margin: "16px 0" } as const;

/**
 * Internal, no customer selected. Deliberately NOT the customer dashboard
 * with a different data source: summarize_aggregate() returns no by_day
 * series, so there is no chart to draw. Stat cards plus a per-client table
 * that clicks through.
 */
function AggregateView({ data }: { data: AggregateSummary }) {
  const navigate = useNavigate();
  const s = data.statistics;
  return (
    <>
      <div className="stat-row" style={statRow}>
        <StatCard label="Transactions" value={s.accepted.count} tone="accent" />
        <StatCard label="Reports submitted" value={s.submitted_to_fintrac.report_count} tone="accent" />
        <StatCard label="Pending" value={s.pending.report_count} />
        <StatCard label="Customers with activity" value={data.by_client.length} />
      </div>
      {data.by_client.length === 0 ? (
        <p className="empty">No activity across any customer in this range.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr><th>Customer</th><th>Transactions</th><th>Submitted</th><th>Pending</th><th>Errors</th></tr>
          </thead>
          <tbody>
            {data.by_client.map((c) => (
              <tr key={c.client_id} className="clickable"
                  onClick={() => navigate(`/customers/${c.client_id}/`)}>
                <td className="mono">{c.client_id}</td>
                <td>{c.accepted.count}</td>
                <td>{c.submitted.report_count}</td>
                <td>{c.pending.report_count}</td>
                <td className={c.errors > 0 ? "bad" : undefined}>{c.errors}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

interface DashboardPageProps {
  /** Set when an internal user is inside a customer's context. Customer-role
   *  users never pass this -- their scope comes from their own JWT. */
  clientId?: string;
  /** True for an internal user with no customer selected: render the
   *  cross-customer rollup instead. */
  aggregate?: boolean;
}

export function DashboardPage({ clientId, aggregate }: DashboardPageProps = {}) {
  const [{ from, to }, setRange] = useState(defaultRange());

  const scope = clientId ? `&client_id=${encodeURIComponent(clientId)}` : "";
  // Absolute, not relative: under v7_relativeSplatPath a relative link from
  // the scoped index route resolves against the URL, not the route.
  const base = clientId ? `/customers/${encodeURIComponent(clientId)}` : "";
  const range = `from=${from}&to=${to}`;

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", from, to, clientId ?? null, Boolean(aggregate)],
    queryFn: () => apiFetch<DashboardSummary | AggregateSummary>(
      `/portal/summary?from=${from}&to=${to}${scope}`),
    enabled: Boolean(from && to),
  });

  // The rejection cards and the banner come from the endpoint behind the
  // Rejections page itself, so a card's number is what its click-through
  // shows. Not for the aggregate view: that endpoint is per-customer.
  const { data: rej } = useQuery({
    queryKey: ["rejections-summary", from, to, clientId ?? null],
    queryFn: () => apiFetch<RejectionsSummary>(
      `/portal/rejections/summary?from=${from}&to=${to}${scope}`),
    enabled: Boolean(from && to) && !aggregate,
  });

  const cards = !aggregate && data ? computeDashboardCards(data as DashboardSummary) : null;
  const rejOk = isRejectionsSummary(rej);
  const rejCards = rejOk ? computeRejectionCards(rej) : null;
  const banner = rejOk ? fintracBannerText(rej) : null;

  return (
    <div>
      {aggregate && <h2>All customers</h2>}
      <DateRangePicker from={from} to={to} onChange={(f, t) => setRange({ from: f, to: t })} />
      {isLoading && <p>Loading…</p>}
      {aggregate && data && <AggregateView data={data as AggregateSummary} />}
      {banner && (
        <Link to={`${base}/rejections?${range}`} className="alert-banner bad" role="alert">
          {banner}. Review rejections →
        </Link>
      )}
      {cards && (
        <>
          <div className="stat-row" style={statRow}>
            <StatCard label="Transactions" value={cards.transactionsAccepted} tone="accent"
                      to={`${base}/transactions`} />
            <StatCard
              label="Filed w/ FINTRAC"
              value={cards.filedTotal}
              sub={[
                ...Object.entries(cards.filedByType).map(([t, n]) => `${t} ${n}`),
                ...(cards.withdrawnTotal > 0 ? [`${cards.withdrawnTotal} withdrawn`] : []),
              ].join(" · ")}
              tone="accent"
              to={`${base}/reports`}
            />
            <StatCard
              label="Rejected by FINTRAC"
              value={rejCards?.fintracRejected ?? 0}
              sub={rejCards && rejCards.pipelineErrors > 0
                ? `+ ${rejCards.pipelineErrors} delayed on our side` : undefined}
              tone={rejCards && rejCards.fintracRejected > 0 ? "bad" : undefined}
              to={`${base}/rejections?${range}`}
            />
            <StatCard label="Rejected rows" value={rejCards?.rejectedRows ?? 0}
                      tone={rejCards && rejCards.rejectedRows > 0 ? "warn" : undefined}
                      to={`${base}/rejections?${range}`} />
          </div>
          <div className="card" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data as DashboardSummary).by_day}>
                <XAxis dataKey="date" stroke="var(--dim)" fontSize={11} />
                <YAxis stroke="var(--dim)" fontSize={11} />
                <Tooltip contentStyle={{ background: "var(--card2)", border: "1px solid var(--line)" }} />
                <Bar dataKey="accepted.count" fill="var(--teal)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
